import { api } from "./client";
import type { ApiResponse } from "./types";

// ─── Shapes ─────────────────────────────────────────────────────────
// Mirrors apps/api/src/modules/bazaar/bazaar.service.ts return maps.
//
// `status` and `kind` are STRINGS, not unions, deliberately: the API sends
// them as open strings so a value we have not seen renders instead of blanking
// the screen. Narrowing them here would reintroduce exactly the breakage the
// server went out of its way to avoid.

export interface HaatFestival {
  id: string;
  name: string;
  slug: string;
  date: string;
}

export interface HaatSummary {
  id: string;
  slug: string;
  name: string;
  metroCode: string;
  opensAt: string;
  closesAt: string;
  /** upcoming | open | closed — open string. */
  status: string;
  /** The season, always renderable: a festival's name, or "Wedding season". */
  occasion: string | null;
  acceptedCategories: string[];
  /** Every stall in this haat is free this season: no plan is asked for. */
  stallsFree: boolean;
  festival: HaatFestival | null;
  stallCount: number;
}

export interface BazaarShelf {
  code: string;
  name: string;
  icon: string | null;
  /** service | goods | both — open string. */
  kind: string;
  /** Zero is a real answer: the shelf exists and is empty. */
  shopCount: number;
}

export interface BazaarEntrance {
  city: string | null;
  metro: { code: string; name: string } | null;
  haats: HaatSummary[];
  shelves: BazaarShelf[];
  shopCount: number;
}

export interface StallItem {
  id: string;
  itemId: string;
  name: string;
  priceInfo: string | null;
  imageUrl: string | null;
  videoUrl: string | null;
  sortOrder: number;
}

export interface Stall {
  id: string;
  headline: string | null;
  provider: {
    id: string;
    slug: string;
    name: string;
    city: string;
    state: string;
    isVerified: boolean;
    vouchCount: number;
    avgRating: number;
    category: { code: string; name: string; icon: string | null } | null;
  };
  items: StallItem[];
  yashOffer: { id: string; title: string } | null;
}

export interface HaatDetail extends Omit<HaatSummary, "stallCount"> {
  /** SHUFFLED per request. Render in the order given — never re-sort. */
  stalls: Stall[];
}

export interface StallOption {
  providerId: string;
  name: string;
  city: string;
  stallId: string | null;
  canTakeStall: boolean;
  /** Open string: STALL_EXISTS | HAAT_CLOSED | CATEGORY_NOT_STALLABLE | … */
  reasonCode: string | null;
  reason: string | null;
  feeWaived: boolean;
}

// ─── Calls ──────────────────────────────────────────────────────────

/**
 * The entrance. Takes a CITY, not a metro code — the server resolves the metro
 * once so the shelf counts and the directory cannot disagree. Signed in, you
 * can omit the city and get your own.
 */
export function getEntrance(params?: { city?: string; state?: string }) {
  const q = new URLSearchParams();
  if (params?.city) q.set("city", params.city);
  if (params?.state) q.set("state", params.state);
  const qs = q.toString();
  return api.get<ApiResponse<BazaarEntrance>>(`/bazaar${qs ? `?${qs}` : ""}`);
}

export function listHaats(params?: { metroCode?: string; includeClosed?: boolean }) {
  const q = new URLSearchParams();
  if (params?.metroCode) q.set("metroCode", params.metroCode);
  if (params?.includeClosed) q.set("includeClosed", "true");
  const qs = q.toString();
  return api.get<ApiResponse<{ haats: HaatSummary[] }>>(`/bazaar/haats${qs ? `?${qs}` : ""}`);
}

export function getHaat(slug: string) {
  return api.get<ApiResponse<HaatDetail>>(`/bazaar/haats/${slug}`);
}

/** Which of MY businesses could take a stall here, and why not. */
export function myStallOptions(haatId: string) {
  return api.get<ApiResponse<{ haatId: string; businesses: StallOption[] }>>(
    `/bazaar/haats/${haatId}/my-stalls`,
  );
}

/** Take a stall, as the business. Free first stall only — a second returns 402. */
export function rentStall(haatId: string, input: { providerId: string; headline?: string }) {
  return api.post<ApiResponse<Stall & { feeWaived: boolean; rentCents: number }>>(
    `/bazaar/haats/${haatId}/rent`,
    input,
  );
}

/** Put one of my own catalogue items on my own stall. */
export function stockStall(
  stallId: string,
  input: { providerItemId: string; priceInfo?: string; sortOrder?: number },
) {
  return api.post<ApiResponse<StallItem>>(`/bazaar/stalls/${stallId}/my-items`, input);
}

/** Change the line on my own stall. `null` takes it off. */
export function setStallHeadline(stallId: string, headline: string | null) {
  return api.patch<ApiResponse<Stall>>(`/bazaar/stalls/${stallId}/my-headline`, { headline });
}

/** How my stall did: the note a closed haat's notice opens. Counts only. */
export interface StallSummary {
  stallId: string;
  business: { id: string; name: string };
  haat: { id: string; slug: string; name: string; status: string; opensAt: string; closesAt: string };
  counts: { pageViews: number; contactTaps: number; conversations: number };
  countedUntil: string;
  nextHaat: { id: string; slug: string; name: string; opensAt: string } | null;
}

export function getStallSummary(stallId: string) {
  return api.get<ApiResponse<StallSummary>>(`/bazaar/stalls/${stallId}/summary`);
}

/** Give the stall up. Deactivated, not deleted — the waiver stays spent. */
export function releaseStall(stallId: string) {
  return api.delete<ApiResponse<{ released: boolean }>>(`/bazaar/stalls/${stallId}`);
}
